import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/sharp-solid-svg-icons';
import { type FC, type PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Typography from '@/components/typography';
import { SelectedFileOverflow } from '@/features/selectionsPanel/selectedFileOverflow';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { pluralize } from '@/utils/pluralize';

type Props = PropsWithChildren & {
  headerHeightInPx?: number;
};

export const SelectionsPanelHeader: FC<Props> = ({
  headerHeightInPx = undefined,
  children = undefined,
}) => {
  const { activeFile } = useFileManagementContext();

  const { isPanelOpen, setIsPanelOpen, totalSelectionCount } =
    useSelectionsPanelContext();

  return (
    <div
      className="sticky top-0 z-10 flex w-full flex-col gap-3 border-b border-gray-20 bg-white p-3"
      style={{
        maxHeight: headerHeightInPx ? `${headerHeightInPx}px` : undefined,
        minHeight: headerHeightInPx ? `${headerHeightInPx}px` : undefined,
      }}
    >
      <div className="flex max-w-full flex-row flex-nowrap items-center">
        <div className="max-w-[calc(100%-64px)] grow">
          {activeFile && (
            <Typography className="truncate" variant="h5">
              {activeFile.name || 'Unnamed'}
            </Typography>
          )}
          <Typography variant={activeFile ? 'body2' : 'h5'}>
            {pluralize('Selection', totalSelectionCount)}
          </Typography>
        </div>

        <div className="ml-auto flex flex-row flex-nowrap">
          {activeFile && <SelectedFileOverflow file={activeFile} />}
          <IconButton
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={() => setIsPanelOpen((prev) => !prev)}
          >
            <FontAwesomeIcon
              className={twMerge(
                'text-gray-100 transition',
                isPanelOpen && 'rotate-180'
              )}
              icon={faChevronDown}
            />
          </IconButton>
        </div>
      </div>
      {isPanelOpen && children}
    </div>
  );
};
